/**
 * Central sync orchestrator for uploading offline data (quotes, meetings) to API
 * Handles retry logic, conflict resolution, and batch processing
 * Following OBY api-conventions.json: proper status codes, error handling
 */

import { uploadImageToR2 } from "../imageUtils";
import {
  cleanupSyncedImages,
  deleteOfflineImage,
  getPendingSyncImages,
  markImageFailed,
  markImageSynced,
  markImageSyncing,
} from "../offline/image-storage";
import {
  getPendingSyncMeetings,
  markMeetingFailed,
  markMeetingSynced,
  markMeetingSyncing,
  removeOfflineMeeting,
} from "../offline/meeting-storage";
import {
  deleteOfflineQuote,
  getPendingSyncQuotes,
  markQuoteFailed,
  markQuoteSynced,
  markQuoteSyncing,
} from "../offline/quote-storage";
import type { OfflineImage, OfflineMeeting, OfflineQuote } from "../offline/types";
import { isOnline, testServerReachability } from "./network-detector";
import { dequeue, getRetryableItems, markFailed, markRetryAttempt } from "./queue";

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ id: string; type: "quote" | "meeting"; error: string }>;
}

type SyncCallback = (result: SyncResult) => void;

const syncListeners = new Set<SyncCallback>();
let isSyncing = false;

/**
 * Register callback to be notified of sync results
 */
export function onSyncComplete(callback: SyncCallback): () => void {
  syncListeners.add(callback);
  return () => syncListeners.delete(callback);
}

/**
 * Check if sync is currently in progress
 */
export function getIsSyncing(): boolean {
  return isSyncing;
}

/**
 * Sync a single offline image to R2
 * Uploads the blob and returns the R2 URL
 */
async function syncSingleImage(
  image: OfflineImage
): Promise<{ success: boolean; error?: string; url?: string }> {
  try {
    // Mark as syncing
    await markImageSyncing(image.id);

    // Extract projectId from quote_id (format: "offline_project_{projectId}")
    const projectId = image.quote_id.replace("offline_project_", "");

    // Upload blob to R2
    const uploadResult = await uploadImageToR2(image.blob, projectId);

    // Mark as synced with URL
    await markImageSynced(image.id, uploadResult.url);

    // Delete from IndexedDB to free space
    await deleteOfflineImage(image.id);

    return { success: true, url: uploadResult.url };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    await markImageFailed(image.id, errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Sync a single offline quote to Supabase
 */
async function syncSingleQuote(quote: OfflineQuote): Promise<{ success: boolean; error?: string }> {
  try {
    // Mark as syncing
    await markQuoteSyncing(quote.id);

    // Prepare quote data for API
    const quotePayload = {
      project_id: quote.project_id,
      checklist_template_id: quote.checklist_template_id,
      quote_data: quote.quote_data,
      total_amount: quote.total_amount,
      quote_name: `Quote - ${new Date(quote.created_at).toLocaleDateString()}`,
      status: "draft" as const,
    };

    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

    // Insert via API
    const response = await fetch(`${apiUrl}/api/v1/checklist-quotes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(quotePayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Failed to sync quote: ${response.statusText}`;
      await markQuoteFailed(quote.id, errorMessage);
      return { success: false, error: errorMessage };
    }

    const _data = await response.json();

    // Mark as synced and remove from offline storage
    await markQuoteSynced(quote.id);
    await deleteOfflineQuote(quote.id);
    await dequeue(quote.id);

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    await markQuoteFailed(quote.id, errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Sync a single offline meeting to API
 */
async function syncSingleMeeting(
  meeting: OfflineMeeting
): Promise<{ success: boolean; error?: string }> {
  try {
    // Mark as syncing
    await markMeetingSyncing(meeting.id);

    // Prepare meeting data for API
    const meetingPayload = {
      customer_id: meeting.customer_id,
      project_id: meeting.project_id,
      meeting_date: meeting.meeting_date,
      meeting_time: meeting.meeting_time,
      duration_minutes: meeting.duration_minutes,
      event_type: meeting.event_type,
      meeting_type: meeting.meeting_type,
      status: meeting.status,
      location: meeting.location,
      notes: meeting.notes,
    };

    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787";

    // Insert via API
    const response = await fetch(`${apiUrl}/api/v1/meetings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(meetingPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Failed to sync meeting: ${response.statusText}`;
      await markMeetingFailed(meeting.id, errorMessage);
      return { success: false, error: errorMessage };
    }

    const _data = await response.json();

    // Mark as synced and remove from offline storage
    await markMeetingSynced(meeting.id);
    await removeOfflineMeeting(meeting.id);
    await dequeue(meeting.id);

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    await markMeetingFailed(meeting.id, errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Sync all pending quotes
 */
export async function syncPendingQuotes(): Promise<SyncResult> {
  if (isSyncing) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Sync already in progress" }],
    };
  }

  // Check if online
  if (!isOnline()) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Device is offline" }],
    };
  }

  // Test server reachability
  const reachable = await testServerReachability();
  if (!reachable) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Server unreachable" }],
    };
  }

  isSyncing = true;

  try {
    const pendingQuotes = await getPendingSyncQuotes();

    if (pendingQuotes.length === 0) {
      isSyncing = false;
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    const results: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    // Sync quotes one by one (could be parallelized but safer sequentially)
    for (const quote of pendingQuotes) {
      const result = await syncSingleQuote(quote);

      if (result.success) {
        results.synced++;
      } else {
        results.failed++;
        results.errors.push({
          id: quote.id,
          type: "quote",
          error: result.error || "Unknown error",
        });
      }
    }

    results.success = results.failed === 0;

    // Notify listeners
    syncListeners.forEach((callback) => {
      callback(results);
    });

    isSyncing = false;
    return results;
  } catch (error) {
    isSyncing = false;

    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: errorMessage }],
    };
  }
}

/**
 * Retry failed quotes that are ready for retry
 */
export async function retryFailedQuotes(): Promise<SyncResult> {
  if (isSyncing) {
    return { success: false, synced: 0, failed: 0, errors: [] };
  }

  // Check if online
  if (!isOnline()) {
    return { success: false, synced: 0, failed: 0, errors: [] };
  }

  const reachable = await testServerReachability();
  if (!reachable) {
    return { success: false, synced: 0, failed: 0, errors: [] };
  }

  isSyncing = true;

  try {
    const retryableItems = await getRetryableItems();

    if (retryableItems.length === 0) {
      isSyncing = false;
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    const results: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    // Get the actual quotes and meetings from storage
    const pendingQuotes = await getPendingSyncQuotes();
    const pendingMeetings = await getPendingSyncMeetings();

    for (const item of retryableItems) {
      let result: { success: boolean; error?: string };

      // Determine if this is a quote or meeting based on the item type
      if (item.type === "meeting") {
        const meeting = pendingMeetings.find((m) => m.id === item.id);
        if (!meeting) {
          await dequeue(item.id);
          continue;
        }

        await markRetryAttempt(item);
        result = await syncSingleMeeting(meeting);

        if (result.success) {
          results.synced++;
        } else {
          results.failed++;
          await markFailed(item, result.error || "Unknown error");
          results.errors.push({
            id: meeting.id,
            type: "meeting",
            error: result.error || "Unknown error",
          });
        }
      } else {
        // Default to quote for backward compatibility
        const quote = pendingQuotes.find((q) => q.id === item.id);
        if (!quote) {
          await dequeue(item.id);
          continue;
        }

        await markRetryAttempt(item);
        result = await syncSingleQuote(quote);

        if (result.success) {
          results.synced++;
        } else {
          results.failed++;
          await markFailed(item, result.error || "Unknown error");
          results.errors.push({
            id: quote.id,
            type: "quote",
            error: result.error || "Unknown error",
          });
        }
      }
    }

    results.success = results.failed === 0;

    syncListeners.forEach((callback) => {
      callback(results);
    });

    isSyncing = false;
    return results;
  } catch (error) {
    isSyncing = false;

    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: errorMessage }],
    };
  }
}

/**
 * Sync all pending meetings
 */
export async function syncPendingMeetings(): Promise<SyncResult> {
  if (isSyncing) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "meeting", error: "Sync already in progress" }],
    };
  }

  // Check if online
  if (!isOnline()) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "meeting", error: "Device is offline" }],
    };
  }

  // Test server reachability
  const reachable = await testServerReachability();
  if (!reachable) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "meeting", error: "Server unreachable" }],
    };
  }

  isSyncing = true;

  try {
    const pendingMeetings = await getPendingSyncMeetings();

    if (pendingMeetings.length === 0) {
      isSyncing = false;
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    const results: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    // Sync meetings one by one (could be parallelized but safer sequentially)
    for (const meeting of pendingMeetings) {
      const result = await syncSingleMeeting(meeting);

      if (result.success) {
        results.synced++;
      } else {
        results.failed++;
        results.errors.push({
          id: meeting.id,
          type: "meeting",
          error: result.error || "Unknown error",
        });
      }
    }

    results.success = results.failed === 0;

    // Notify listeners
    syncListeners.forEach((callback) => {
      callback(results);
    });

    isSyncing = false;
    return results;
  } catch (error) {
    isSyncing = false;

    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "meeting", error: errorMessage }],
    };
  }
}

/**
 * Sync all pending images
 * CRITICAL: This must run BEFORE syncing quotes, so quotes have real R2 URLs
 */
export async function syncPendingImages(): Promise<SyncResult> {
  if (isSyncing) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Sync already in progress" }],
    };
  }

  // Check if online
  if (!isOnline()) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Device is offline" }],
    };
  }

  // Test server reachability
  const reachable = await testServerReachability();
  if (!reachable) {
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: "Server unreachable" }],
    };
  }

  isSyncing = true;

  try {
    const pendingImages = await getPendingSyncImages();

    if (pendingImages.length === 0) {
      isSyncing = false;
      return { success: true, synced: 0, failed: 0, errors: [] };
    }

    const results: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    // Sync images one by one (safer than parallel for large files)
    for (const image of pendingImages) {
      const result = await syncSingleImage(image);

      if (result.success) {
        results.synced++;
      } else {
        results.failed++;
        results.errors.push({
          id: image.id,
          type: "quote",
          error: result.error || "Unknown error",
        });
      }
    }

    results.success = results.failed === 0;

    // Clean up synced images to free space
    await cleanupSyncedImages();

    // Notify listeners
    syncListeners.forEach((callback) => {
      callback(results);
    });

    isSyncing = false;
    return results;
  } catch (error) {
    isSyncing = false;

    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return {
      success: false,
      synced: 0,
      failed: 0,
      errors: [{ id: "", type: "quote", error: errorMessage }],
    };
  }
}

/**
 * Sync all pending data (images, quotes, and meetings)
 * IMAGES FIRST: Images must be uploaded before quotes, so quotes have real R2 URLs
 */
export async function syncAll(): Promise<SyncResult> {
  const combinedResults: SyncResult = {
    success: true,
    synced: 0,
    failed: 0,
    errors: [],
  };

  // Sync images FIRST (critical for quotes to have real URLs)
  const imageResults = await syncPendingImages();
  combinedResults.synced += imageResults.synced;
  combinedResults.failed += imageResults.failed;
  combinedResults.errors.push(...imageResults.errors);

  // Then sync quotes (now with real image URLs)
  const quoteResults = await syncPendingQuotes();
  combinedResults.synced += quoteResults.synced;
  combinedResults.failed += quoteResults.failed;
  combinedResults.errors.push(...quoteResults.errors);

  // Finally sync meetings
  const meetingResults = await syncPendingMeetings();
  combinedResults.synced += meetingResults.synced;
  combinedResults.failed += meetingResults.failed;
  combinedResults.errors.push(...meetingResults.errors);

  combinedResults.success = combinedResults.failed === 0;

  return combinedResults;
}

/**
 * Initialize sync manager (register for background sync if available)
 */
export function initializeSyncManager(): void {
  // Register for Background Sync API if available
  if ("serviceWorker" in navigator && "sync" in ServiceWorkerRegistration.prototype) {
    navigator.serviceWorker.ready
      .then((registration) => {
        // Register multiple sync tags for different data types
        return Promise.all([
          registration.sync.register("sync-quotes"),
          registration.sync.register("sync-meetings"),
        ]);
      })
      .catch((_error) => {
        // Ignore Background Sync registration errors (not supported in all browsers)
      });
  }
}

/**
 * Manually trigger sync (for user-initiated sync)
 */
export async function triggerManualSync(): Promise<SyncResult> {
  return await syncAll();
}
